import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home", testId: "nav-home-link" },
  { to: "/services", label: "Services", testId: "nav-services-link" },
  { to: "/about", label: "About", testId: "nav-about-link" },
  { to: "/gallery", label: "Field Work", testId: "nav-gallery-link" },
  { to: "/calculator", label: "Assessment Tool", testId: "nav-calculator-link" },
  { to: "/contact", label: "Contact", testId: "nav-contact-link" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed inset-x-0 top-0 z-50 border-b border-thermal/15 bg-obsidian/80 backdrop-blur-xl"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-2 sm:px-6 lg:px-8">
          <Link to="/" data-testid="nav-logo-link" className="group flex items-center gap-3">
            <span className="flex items-center transition-transform duration-300 group-hover:scale-[1.03]">
              <img src="/logo-wide.png" alt="Victorian Pest and Game Services logo" className="h-12 w-auto" />
            </span>
          </Link>

          <nav className="hidden items-center gap-7 lg:flex" data-testid="nav-desktop-menu">
            {LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={link.testId}
                className={({ isActive }) =>
                  `font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                    isActive ? "text-thermal" : "text-euca hover:text-bone"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="nav-request-assessment-button"
              className="hidden items-center gap-2 border border-thermal bg-thermal px-4 py-2 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-obsidian transition-all duration-300 hover:bg-transparent hover:text-thermal sm:flex"
            >
              Request Assessment
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
            <button
              data-testid="nav-mobile-menu-button"
              onClick={() => setOpen(!open)}
              className="flex h-9 w-9 items-center justify-center border border-thermal/30 text-bone lg:hidden"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-obsidian/97 px-8 pt-16 backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <nav className="flex flex-col gap-2">
              {LINKS.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                >
                  <NavLink
                    to={link.to}
                    data-testid={`nav-mobile-${link.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `block border-b border-thermal/10 py-4 font-display text-3xl font-bold uppercase tracking-tight ${
                        isActive ? "text-thermal" : "text-bone"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.3em] text-euca">
              Licensed · Insured · Humane — VIC &amp; Southern NSW
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
